import { useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Columns3,
  LayoutGrid,
  LayoutList,
  Pencil,
  X,
  Save,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Pagination from '@/components/ui/Pagination';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { ColumnDef, DataTableState, BulkAction, PaginationData, DirtyCell } from './types';

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  pagination: PaginationData;
  isLoading: boolean;
  error?: string | null;
  state: DataTableState;
  onStateChange: (partial: Partial<DataTableState>) => void;
  rowKey: keyof T | ((row: T) => string);
  onInlineEdit?: (rowId: string, field: string, value: unknown) => Promise<void>;
  onBatchSave?: (changes: DirtyCell[]) => Promise<void>;
  onDirtyChange?: (rowId: string, columnId: string, value: unknown, originalValue: unknown) => void;
  bulkActions?: BulkAction<T>[];
  gridCard?: (row: T) => ReactNode;
  emptyState?: ReactNode;
  pageSizeOptions?: number[];
  searchPlaceholder?: string;
}

function getRowId<T>(row: T, rowKey: keyof T | ((row: T) => string)): string {
  return typeof rowKey === 'function' ? rowKey(row) : String(row[rowKey]);
}

function getCellValue<T>(row: T, column: ColumnDef<T>): unknown {
  if (column.accessorFn) return column.accessorFn(row);
  if (column.accessorKey) return row[column.accessorKey];
  return undefined;
}

function dirtyCellKey(rowId: string, columnId: string) {
  return `${rowId}::${columnId}`;
}

const editInputClasses = 'w-full rounded border border-stone-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500';

export default function DataTable<T>({
  columns,
  data,
  pagination,
  isLoading,
  error,
  state,
  onStateChange,
  rowKey,
  onInlineEdit,
  onBatchSave,
  onDirtyChange,
  bulkActions,
  gridCard,
  emptyState,
  pageSizeOptions = [10, 25, 50, 100],
  searchPlaceholder = 'Search...',
}: DataTableProps<T>) {
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editInputRefs = useRef<Map<string, HTMLInputElement | HTMLSelectElement>>(new Map());

  const visibleCols = useMemo(
    () => columns.filter((c) => state.visibleColumns.includes(c.id)),
    [columns, state.visibleColumns]
  );

  const allSelected = useMemo(
    () => data.length > 0 && data.every((row) => state.selectedIds.has(getRowId(row, rowKey))),
    [data, state.selectedIds, rowKey]
  );
  const someSelected = useMemo(
    () => data.some((row) => state.selectedIds.has(getRowId(row, rowKey))),
    [data, state.selectedIds, rowKey]
  );

  const hasDirtyChanges = state.dirtyCells.size > 0;
  const editingColumns = state.editingColumns;

  // Focus first input when column editing starts
  useEffect(() => {
    if (editingColumns.size > 0) {
      // Focus the first visible input
      for (const [, ref] of editInputRefs.current) {
        if (ref) {
          ref.focus();
          break;
        }
      }
    }
  }, [editingColumns.size]);

  const handleSort = useCallback(
    (columnId: string) => {
      if (state.sort === columnId) {
        onStateChange({ order: state.order === 'asc' ? 'desc' : 'asc' });
      } else {
        onStateChange({ sort: columnId, order: 'asc' });
      }
    },
    [state.sort, state.order, onStateChange]
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onStateChange({ selectedIds: new Set() });
    } else {
      const ids = new Set(data.map((row) => getRowId(row, rowKey)));
      onStateChange({ selectedIds: ids });
    }
  }, [allSelected, data, rowKey, onStateChange]);

  const handleSelectRow = useCallback(
    (rowId: string) => {
      const newSet = new Set(state.selectedIds);
      if (newSet.has(rowId)) newSet.delete(rowId);
      else newSet.add(rowId);
      onStateChange({ selectedIds: newSet });
    },
    [state.selectedIds, onStateChange]
  );

  const handleToggleColumnEdit = useCallback(
    (columnId: string) => {
      const next = new Set(editingColumns);
      if (next.has(columnId)) {
        next.delete(columnId);
        // Remove dirty cells for this column when cancelling
        if (state.dirtyCells.size > 0) {
          const nextDirty = new Map(state.dirtyCells);
          for (const [key, cell] of nextDirty) {
            if (cell.columnId === columnId) nextDirty.delete(key);
          }
          onStateChange({ editingColumns: next, dirtyCells: nextDirty });
          return;
        }
      } else {
        next.add(columnId);
      }
      onStateChange({ editingColumns: next });
    },
    [editingColumns, state.dirtyCells, onStateChange]
  );

  const handleCellChange = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      if (onDirtyChange) {
        onDirtyChange(rowId, columnId, value, originalValue);
      } else {
        // Default: update dirtyCells in state
        const nextDirty = new Map(state.dirtyCells);
        const key = dirtyCellKey(rowId, columnId);
        if (String(value) === String(originalValue)) {
          nextDirty.delete(key);
        } else {
          nextDirty.set(key, { rowId, columnId, value, originalValue });
        }
        onStateChange({ dirtyCells: nextDirty });
      }
    },
    [onDirtyChange, state.dirtyCells, onStateChange]
  );

  const handleSaveAll = useCallback(async () => {
    if (state.dirtyCells.size === 0) return;
    setIsSaving(true);
    try {
      if (onBatchSave) {
        await onBatchSave(Array.from(state.dirtyCells.values()));
      } else if (onInlineEdit) {
        // Fall back to individual saves
        for (const cell of state.dirtyCells.values()) {
          await onInlineEdit(cell.rowId, cell.columnId, cell.value);
        }
      }
      onStateChange({ dirtyCells: new Map(), editingColumns: new Set() });
    } catch {
      // Keep dirty state on error
    } finally {
      setIsSaving(false);
    }
  }, [state.dirtyCells, onBatchSave, onInlineEdit, onStateChange]);

  const handleDiscardAll = useCallback(() => {
    onStateChange({ dirtyCells: new Map(), editingColumns: new Set() });
  }, [onStateChange]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Discard changes for the current column
        const target = e.currentTarget;
        const columnId = target.getAttribute('data-column-id');
        if (columnId) handleToggleColumnEdit(columnId);
      }
    },
    [handleToggleColumnEdit]
  );

  const renderSortIcon = (columnId: string) => {
    if (state.sort !== columnId) {
      return <ArrowUpDown className="w-4 h-4 text-stone-400" />;
    }
    return state.order === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-blue-600" />
    ) : (
      <ChevronDown className="w-4 h-4 text-blue-600" />
    );
  };

  const renderEditInput = (row: T, column: ColumnDef<T>) => {
    const id = getRowId(row, rowKey);
    const originalValue = getCellValue(row, column);
    const key = dirtyCellKey(id, column.id);
    const dirtyCell = state.dirtyCells.get(key);
    const currentValue = dirtyCell ? dirtyCell.value : originalValue;
    const type = column.editType || 'text';
    const refKey = `${id}-${column.id}`;

    if (type === 'select' && column.editOptions) {
      return (
        <select
          ref={(el) => { if (el) editInputRefs.current.set(refKey, el); else editInputRefs.current.delete(refKey); }}
          value={String(currentValue ?? '')}
          onChange={(e) => handleCellChange(id, column.id, e.target.value, originalValue)}
          onKeyDown={handleEditKeyDown}
          data-column-id={column.id}
          className={editInputClasses}
        >
          {column.editOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        ref={(el) => { if (el) editInputRefs.current.set(refKey, el); else editInputRefs.current.delete(refKey); }}
        type={type === 'number' ? 'number' : 'text'}
        value={String(currentValue ?? '')}
        onChange={(e) => handleCellChange(id, column.id, type === 'number' ? e.target.value : e.target.value, originalValue)}
        onKeyDown={handleEditKeyDown}
        step={type === 'number' ? '0.01' : undefined}
        data-column-id={column.id}
        className={editInputClasses}
      />
    );
  };

  const renderCell = (row: T, column: ColumnDef<T>) => {
    const id = getRowId(row, rowKey);
    const isColumnEditing = editingColumns.has(column.id);

    // If this column is in edit mode, show input
    if (isColumnEditing && column.editable && (onInlineEdit || onBatchSave)) {
      const key = dirtyCellKey(id, column.id);
      const isDirty = state.dirtyCells.has(key);
      return (
        <div className={isDirty ? 'border-l-2 border-amber-400 pl-1 -ml-1' : ''}>
          {renderEditInput(row, column)}
        </div>
      );
    }

    const value = getCellValue(row, column);

    // Custom cell renderer
    if (column.cell) {
      return column.cell({
        row,
        value,
        isEditing: false,
        onChange: () => {},
      });
    }

    const displayValue = value != null ? String(value) : '';
    return <span className="truncate block max-w-xs" title={displayValue}>{displayValue}</span>;
  };

  // Bulk action execution with optional confirmation
  const [confirmingAction, setConfirmingAction] = useState<BulkAction<T> | null>(null);

  const executeBulkAction = async (action: BulkAction<T>) => {
    const selectedRows = data.filter((row) => state.selectedIds.has(getRowId(row, rowKey)));
    await action.onExecute(selectedRows);
    onStateChange({ selectedIds: new Set() });
    setConfirmingAction(null);
  };

  const hasEditableColumns = columns.some((c) => c.editable) && (onInlineEdit || onBatchSave);

  return (
    <div className="space-y-0">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* Left: Search */}
        <div className="flex-1 min-w-[200px] max-w-sm">
          <input
            type="text"
            value={state.search}
            onChange={(e) => onStateChange({ search: e.target.value })}
            placeholder={searchPlaceholder}
            aria-label="Search"
            className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-2">
          {/* Page size selector */}
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-stone-500 whitespace-nowrap">Show</label>
            <select
              value={state.limit}
              onChange={(e) => onStateChange({ limit: parseInt(e.target.value, 10) })}
              aria-label="Items per page"
              className="rounded border border-stone-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* View toggle */}
          {gridCard && (
            <div className="flex border border-stone-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => onStateChange({ viewMode: 'table' })}
                className={`p-1.5 ${state.viewMode === 'table' ? 'bg-stone-200 text-stone-800' : 'bg-white text-stone-500 hover:bg-stone-50'}`}
                title="Table view"
                aria-label="Table view"
                aria-pressed={state.viewMode === 'table'}
              >
                <LayoutList className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onStateChange({ viewMode: 'grid' })}
                className={`p-1.5 ${state.viewMode === 'grid' ? 'bg-stone-200 text-stone-800' : 'bg-white text-stone-500 hover:bg-stone-50'}`}
                title="Grid view"
                aria-label="Grid view"
                aria-pressed={state.viewMode === 'grid'}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Column customizer */}
          <button
            type="button"
            onClick={() => setShowColumnCustomizer(true)}
            className="p-1.5 rounded-lg border border-stone-300 bg-white text-stone-500 hover:bg-stone-50 hover:text-stone-700"
            title="Customize columns"
            aria-label="Customize columns"
          >
            <Columns3 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {bulkActions && state.selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 mb-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-800">
            {state.selectedIds.size} selected
          </span>
          <div className="flex gap-2">
            {bulkActions.map((action) => (
              <Button
                key={action.id}
                variant={action.variant || 'outline'}
                size="sm"
                type="button"
                onClick={() => {
                  if (action.confirmMessage) {
                    setConfirmingAction(action);
                  } else {
                    executeBulkAction(action);
                  }
                }}
              >
                {action.icon}
                {action.label}
              </Button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onStateChange({ selectedIds: new Set() })}
            className="ml-auto text-sm text-blue-600 hover:text-blue-800"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 mb-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && data.length === 0 && (
        emptyState || (
          <div className="py-12 text-center text-stone-500">
            <p className="text-lg font-medium">No results found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters.</p>
          </div>
        )
      )}

      {/* Grid view */}
      {!isLoading && data.length > 0 && state.viewMode === 'grid' && gridCard && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.map((row) => (
            <div key={getRowId(row, rowKey)}>{gridCard(row)}</div>
          ))}
        </div>
      )}

      {/* Table view */}
      {!isLoading && data.length > 0 && (state.viewMode === 'table' || !gridCard) && (
        <div className="overflow-x-auto border border-stone-200 rounded-lg">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {bulkActions && (
                  <th scope="col" className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={handleSelectAll}
                      className="rounded border-stone-300 text-green-600 focus:ring-green-500"
                      aria-label="Select all rows"
                    />
                  </th>
                )}
                {visibleCols.map((col) => (
                  <th
                    key={col.id}
                    scope="col"
                    className={`px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider ${
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                    } ${col.width || ''}`}
                    aria-sort={
                      col.sortable && state.sort === col.id
                        ? state.order === 'asc' ? 'ascending' : 'descending'
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1">
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.id)}
                          className="flex items-center gap-1 hover:text-stone-700"
                        >
                          {col.header}
                          {renderSortIcon(col.id)}
                        </button>
                      ) : (
                        <span>{col.header}</span>
                      )}
                      {/* Per-column edit pencil */}
                      {hasEditableColumns && col.editable && (
                        <button
                          type="button"
                          onClick={() => handleToggleColumnEdit(col.id)}
                          className={`p-0.5 rounded transition-colors ${
                            editingColumns.has(col.id)
                              ? 'text-green-600 bg-green-50 hover:bg-green-100'
                              : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'
                          }`}
                          title={editingColumns.has(col.id) ? `Stop editing ${col.header}` : `Edit ${col.header}`}
                          aria-label={editingColumns.has(col.id) ? `Stop editing ${col.header}` : `Edit ${col.header}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-stone-200">
              {data.map((row) => {
                const id = getRowId(row, rowKey);
                return (
                  <tr
                    key={id}
                    className={`hover:bg-stone-50 transition-colors ${
                      state.selectedIds.has(id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    {bulkActions && (
                      <td className="w-10 px-3 py-3">
                        <input
                          type="checkbox"
                          checked={state.selectedIds.has(id)}
                          onChange={() => handleSelectRow(id)}
                          className="rounded border-stone-300 text-green-600 focus:ring-green-500"
                          aria-label={`Select row ${id}`}
                        />
                      </td>
                    )}
                    {visibleCols.map((col) => (
                      <td
                        key={col.id}
                        className={`px-4 py-3 text-sm text-stone-900 ${
                          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                        }`}
                      >
                        {renderCell(row, col)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating save bar */}
      {hasDirtyChanges && (
        <div className="sticky bottom-4 z-20 mt-4">
          <div className="mx-auto max-w-md flex items-center gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg shadow-lg">
            <span className="text-sm font-medium text-amber-800 flex-1">
              {state.dirtyCells.size} unsaved change{state.dirtyCells.size !== 1 ? 's' : ''}
            </span>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={handleDiscardAll}
              disabled={isSaving}
            >
              <X className="w-4 h-4 mr-1" />
              Discard
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={handleSaveAll}
              disabled={isSaving}
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? 'Saving...' : 'Save All'}
            </Button>
          </div>
        </div>
      )}

      {/* Pagination footer */}
      {!isLoading && pagination.total_pages > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-stone-600">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total}
          </div>
          {pagination.total_pages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              onPageChange={(page) => onStateChange({ page })}
            />
          )}
        </div>
      )}

      {/* Column Customizer Modal */}
      <Modal
        isOpen={showColumnCustomizer}
        onClose={() => setShowColumnCustomizer(false)}
        title="Customize Columns"
      >
        <div className="space-y-2">
          {columns
            .filter((c) => c.hideable !== false)
            .map((col) => (
              <label key={col.id} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-stone-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.visibleColumns.includes(col.id)}
                  onChange={(e) => {
                    const newCols = e.target.checked
                      ? [...state.visibleColumns, col.id]
                      : state.visibleColumns.filter((id) => id !== col.id);
                    onStateChange({ visibleColumns: newCols });
                  }}
                  className="rounded border-stone-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-stone-700">{col.header}</span>
              </label>
            ))}
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-stone-200">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => {
              const defaults = columns.filter((c) => c.defaultVisible !== false).map((c) => c.id);
              onStateChange({ visibleColumns: defaults });
            }}
          >
            Reset to Default
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="button"
            onClick={() => setShowColumnCustomizer(false)}
          >
            Done
          </Button>
        </div>
      </Modal>

      {/* Bulk action confirmation modal */}
      <Modal
        isOpen={!!confirmingAction}
        onClose={() => setConfirmingAction(null)}
        title="Confirm Action"
      >
        <div className="space-y-4">
          <p className="text-stone-700">{confirmingAction?.confirmMessage}</p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="sm" type="button" onClick={() => setConfirmingAction(null)}>
              Cancel
            </Button>
            <Button
              variant={confirmingAction?.variant === 'danger' ? 'danger' : 'primary'}
              size="sm"
              type="button"
              onClick={() => confirmingAction && executeBulkAction(confirmingAction)}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
